import { cn } from '@rocco/utils/cn';
import { ActiveMarker } from '@rocco/components/active-marker';

/**
 * A variant option as the product page sets it: a row of uppercase type with
 * the site header's marker against the selected one.
 *
 * Shared by the page's spec columns and the purchase drawer so a reader meets
 * the same list in both, rather than a list in one and a row of chips in the
 * other.
 */
export const VARIANT_ROW =
    'flex cursor-pointer items-center border-0 bg-transparent p-0 text-left font-sans leading-normal text-[#181818] uppercase transition-opacity hover:opacity-70 max-md:text-[0.75rem] md:text-[0.75rem]';

/** The selected row is the one in medium — the marker does the rest. */
export const VARIANT_ROW_SELECTED = 'font-medium';

/**
 * The row as the drawer sets it: a pill on the page's own light ground, so
 * each option is a target rather than a line of text.
 *
 * The height and corner are the `l` step's, the same one the Add to Cart
 * button below is drawn at, so the column and the action it leads to are cut
 * to one shape. Ranged left and centred in that height, and never shorter
 * than it: the longest names wrap to two lines and the short ones would
 * otherwise leave the grid at two different heights.
 *
 * Set in sentence case rather than the plain list's caps — at this size, in a
 * filled box, caps is a third thing shouting alongside the fill and the label.
 *
 * #f5f5f7 is the ground this page already uses, not a grey invented for these.
 */
export const VARIANT_ROW_PILL =
    // The size is written at both breakpoints because the row it overrides
    // sets it at both — a plain `text-*` would lose to its `md:` half.
    'min-h-[3.25rem] w-full items-center justify-start rounded-[1.375rem] bg-[#f5f5f7] px-6 py-3 text-left leading-none normal-case max-md:text-[0.8125rem] md:text-[0.8125rem]';

/**
 * The selected pill: the same ground the rest sit on, ringed in a mid grey
 * with a white band held between the two.
 *
 * Quiet on purpose. It marks a state, and the drawer already has one thing
 * asking to be pressed.
 *
 * Filled, it was the darkest thing in the drawer and pulled rank on the Add to
 * Cart button below it — a picked option is a state, not the page's action.
 *
 * Both rings are inset shadows rather than borders, so the pill keeps the
 * exact size of the unselected ones and its label does not shift as the choice
 * moves. Written as the property: `shadow-*` funnels one layer through
 * `--tw-shadow`, and a two-layer stack silently resolves to nothing.
 *
 * The ring is listed first because the first shadow paints on top — it takes
 * the outer pixel, and the white band shows through under it.
 *
 * The marker the page's plain list carries is dropped here — the ring already
 * says which one is on, and both together said it twice.
 */
export const VARIANT_ROW_PILL_SELECTED =
    'text-[#323232] [box-shadow:inset_0_0_0_1px_#a0a0a6,inset_0_0_0_3px_#ffffff]';

/** Two to a row, as they fit the drawer's width. */
export const VARIANT_LIST_GRID = 'grid grid-cols-2 gap-1.5';

/** An option the current selection cannot reach. */
export const VARIANT_ROW_UNAVAILABLE = 'text-[#a3a3a3] line-through';

/**
 * The marker, carried by every row so the labels keep their left edge as the
 * selection moves — the nav hides its inactive ones the same way.
 */
export const VariantOptionMarker = ({ selected }: { selected: boolean }) => (
    <ActiveMarker className={cn('mr-1.5', !selected && 'invisible')} />
);
